import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { canUploadNow, creatorOf, useAuth } from "../AuthContext.js";
import { createYoutubeImport } from "../api.js";

export default function YoutubeImport() {
  const navigate = useNavigate();
  const { identity, loading } = useAuth();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return <p className="status-message">Loading…</p>;
  if (!creatorOf(identity)) {
    return (
      <section className="form-page">
        <h1>Become a CoinfallSMP Creator</h1>
        <p className="form-hint">You need a creator account to import videos.</p>
        <div className="button-row">
          <Link to="/login" className="button secondary">
            Log in
          </Link>
          <Link to="/apply" className="button primary">
            Apply for Creator
          </Link>
        </div>
      </section>
    );
  }
  if (!canUploadNow(identity)) {
    return (
      <section className="form-page">
        <h1>Import from YouTube</h1>
        <p className="status-message">
          Upload permission hasn't been granted for your account yet — an admin needs to
          approve your creator account and enable uploads first.
        </p>
      </section>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const video = await createYoutubeImport({
        url: url.trim(),
        title: title.trim() || undefined,
      });
      navigate(`/videos/${video.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
      setSubmitting(false);
    }
  }

  return (
    <section className="form-page">
      <h1>Import from YouTube</h1>
      <p className="form-hint">
        Embeds the original YouTube video — nothing is downloaded or re-hosted, so this
        works instantly and needs no storage of its own. Only import videos CoinfallSMP has
        the rights to feature; if the original video is ever taken down or made private,
        the embed stops working here too.
      </p>
      <form onSubmit={onSubmit}>
        <label>
          YouTube URL
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            disabled={submitting}
            required
          />
        </label>
        <label>
          Title override (optional)
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Defaults to the video's own YouTube title"
            disabled={submitting}
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="button primary" disabled={submitting}>
          {submitting ? "Adding…" : "Add video"}
        </button>
      </form>
    </section>
  );
}
